<template>
    <div
        ref="container"
        class="marquee-container"
        :class="{ 
            paused: isHovered,
            'animation': shouldScroll
        }"
        @mouseenter="pauseOnHover && (isHovered = true)"
        @mouseleave="pauseOnHover && (isHovered = false)"
    >
        <div ref="wrapper" class="marquee-wrapper">
            <div ref="content" class="marquee-content">
                <slot></slot>
            </div>
            <div
                v-if="shouldScroll"
                class="marquee-content"
            >
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, defineProps, watch, getCurrentInstance, computed } from 'vue';
const { proxy } = getCurrentInstance();
const isMobileSize = computed(() => proxy.platform.isMobile);

const props = defineProps({
    speed: {
        type: Number,
        default: 150, // px/s
    },
    pauseOnHover: {
        type: Boolean,
        default: true,
    },
});

const container = ref(null);
const wrapper = ref(null);
const content = ref(null);

const isHovered = ref(false);
const shouldScroll = ref(false); // 👈 控制是否滚动 + 是否复制 dom

let offset = 0;
let lastTimestamp = 0;
let frameId = null;

const step = (timestamp) => {
    if (isHovered.value || !shouldScroll.value) {
        lastTimestamp = 0;
        frameId = requestAnimationFrame(step);
        return;
    }

    if (!lastTimestamp) {
        lastTimestamp = timestamp;
    }
    const delta = (timestamp - lastTimestamp) / 1000;
    lastTimestamp = timestamp;

    offset -= props.speed * delta;

    const contentWidth = content.value?.offsetWidth || 0;
    if (Math.abs(offset) >= contentWidth) {
        offset += contentWidth;
    }

    wrapper.value.style.transform = `translateX(${offset}px)`;
    frameId = requestAnimationFrame(step);
};

const startAnimation = async() => {
    cancelAnimationFrame(frameId);
    offset = 0;
    lastTimestamp = 0;
    await nextTick();

    const contentWidth = content.value?.offsetWidth || 0;
    const containerWidth = container.value?.offsetWidth || 0;

    shouldScroll.value = contentWidth > containerWidth;

    if (!shouldScroll.value) {
        wrapper.value.style.transform = 'translateX(0)';
        return;
    }

    frameId = requestAnimationFrame(step);
};

let resizeTimeout = null;
const onResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
        startAnimation();
    }, 200);
};

const onDocumentClick = (e) => {
    if (!props.pauseOnHover) {
        return; 
    }

    if (container.value?.contains(e.target)) {
        isHovered.value = true;
    } else {
        isHovered.value = false;
    }
};

onMounted(() => {
    startAnimation();
    if (isMobileSize.value) {
        document.addEventListener('click', onDocumentClick);
    } else {
        window.addEventListener('resize', onResize);
    }
});

onBeforeUnmount(() => {
    cancelAnimationFrame(frameId);
    if (isMobileSize.value) {
        document.removeEventListener('click', onDocumentClick);
    } else {
        window.removeEventListener('resize', onResize);
    }
});

watch(() => props.speed, startAnimation);
</script>


<style>
.marquee-container {
    overflow: hidden;
    width: 100%;
    white-space: nowrap;
    display: flex;
    flex-wrap: nowrap;
    position: relative;
}

.marquee-wrapper {
    display: flex;
    white-space: nowrap;
    will-change: transform;
}

.marquee-content {
    flex-shrink: 0;
}
</style>
